import React, { useEffect, useState } from 'react';

import i18n from 'i18n-react';
import type { SweetAlertResult } from 'sweetalert2';

import { FastTrackResultCard } from 'components/FastTrackResultCard/FastTrackResultCard';
import SweetAlert from 'components/SweetAlert';

import APIHandler from 'config/APIHandler';
import { storageKeys } from 'config/settings.json';
import StorageHandler from 'config/StorageHandler';

import { fastTrackData } from 'helpers/FastTrack/fastTrackData';
import getFeatureFlags from 'helpers/getFeatureFlags';
import { legacyRemCss } from 'helpers/legacyRem';

import { useSettings } from 'hooks/useSettings';

interface StoredUser {
  email: string;
  first_name?: string;
  last_name?: string;
  graduated?: boolean;
  training_graduated?: string | null;
}

const setProgressComplete = async (redirectTo?: string) => {
  const user = (await StorageHandler.load(storageKeys.user)) as StoredUser | false;
  const fastTrackFinished = fastTrackData('finished');
  const fastTrackGradeVal = fastTrackData<string>('grade');

  if (user && !user.graduated && fastTrackFinished && fastTrackGradeVal !== null && fastTrackGradeVal !== 'F') {
    const trainingGraduated = fastTrackData<string>('trainingGraduated');
    if (trainingGraduated) {
      user.training_graduated = trainingGraduated;
      StorageHandler.save(storageKeys.user, user);
    }
    StorageHandler.remove(storageKeys.progress);
    if (redirectTo) {
      window.location.href = redirectTo;
    } else {
      window.location.reload();
    }
  }
};

export interface FastTrackPopupsProps {
  onCancelAsk: () => void;
  onConfirmAsk: () => void;
  onConfirmGrade: () => void;
  fastTrackCanceled?: boolean;
  fastTrackErrors?: number;
  fastTrackFinished?: boolean;
  fastTrackInProgress?: boolean;
  fastTrackGrade?: { grade: string; passed?: boolean };
  hasNoFoundationProgress?: boolean;
  hasFastTrack?: boolean;
}

export function FastTrackPopups({
  fastTrackCanceled = false,
  fastTrackErrors = 0,
  fastTrackFinished = false,
  fastTrackGrade,
  fastTrackInProgress = false,
  hasNoFoundationProgress = false,
  hasFastTrack = false,
  onCancelAsk,
  onConfirmAsk,
  onConfirmGrade,
}: FastTrackPopupsProps) {
  const { appId, appInfo, loaded, salesCompanionAppId } = useSettings();
  // '#' ensures the download button renders immediately; replaced with the real URL once the effect resolves.
  const [diplomaUrl, setDiplomaUrl] = useState('#');
  const [hasAchievements, setHasAchievements] = useState(false);

  useEffect(() => {
    void getFeatureFlags()
      .then((flags) => {
        setHasAchievements(flags.has_achievements_rewards_page || flags.badges || flags.metallic_tiers);
      })
      .catch((err: unknown) => {
        console.error('Failed to load feature flags:', err);
      });
  }, []);

  useEffect(() => {
    if (!loaded) return;
    const buildDiplomaUrl = async () => {
      const user = (await StorageHandler.load(storageKeys.user)) as StoredUser | false;
      const flags = await getFeatureFlags();
      if (!user) return;
      const useEu = flags.eu_servers;
      const firstName = user.first_name ?? '';
      const lastName = user.last_name ?? '';
      const { email } = user;
      const appName = appInfo.appName ?? '';
      const imageUrl = useEu
        ? `https://eu.crm.travpromobile.com/diploma_api?firstName=${firstName}&lastName=${lastName}&appID=${String(appId)}&email=${email}`
        : `https://cms.travpromobile.com/api/app/v2/diploma?first_name=${firstName}&last_name=${lastName}&app_id=${String(salesCompanionAppId ?? '')}&email=${email}`;
      const url = APIHandler.getDownloadURL(
        imageUrl,
        `${appName} Diploma ${firstName} ${lastName}.png`.replace(/ /g, '_'),
        'png',
      );
      setDiplomaUrl(url);
    };
    void buildDiplomaUrl().catch((err: unknown) => {
      console.error('Failed to build diploma URL:', err);
    });
  }, [loaded, appId, salesCompanionAppId, appInfo]);

  const grade = fastTrackGrade?.grade ?? '';
  const passed = fastTrackGrade?.passed ?? false;
  const correctAnswers = new Set(fastTrackData<string[]>('correctAnswers') ?? []).size;
  const totalQuestions = fastTrackData<number>('totalQuestions') ?? 0;

  const gradeSwal = {
    html: (
      <FastTrackResultCard
        grade={grade}
        passed={passed}
        correctAnswers={correctAnswers}
        totalQuestions={totalQuestions}
        hasAchievements={hasAchievements}
        diplomaUrl={passed ? diplomaUrl : undefined}
        onComplete={(redirectTo) => {
          onConfirmGrade();
          void setProgressComplete(redirectTo);
        }}
      />
    ),
    confirmButtonText: i18n.translate(passed ? 'FastTrackView.completeFastTrack' : 'FastTrackView.beginFullTraining'),
    showCancelButton: false,
    customClass: {
      confirmButton: passed ? 'ft-grade-swal__confirm--success' : 'ft-grade-swal__confirm--failed',
    },
    onClose: ({ value, dismiss }: SweetAlertResult) => {
      if (value === true || dismiss) {
        onConfirmGrade();
        void setProgressComplete();
      }
    },
  };

  const startSwal = {
    imageUrl: appInfo.logo,
    imageWidth: legacyRemCss(30),
    // TODO: fastTrackPrompt is intentionally app-specific (currently BVI). Should this feature be used for OTHER apps, a more dynamic solution for app-specific messaging should be implemented. Also see the Flag discription in cms.
    title: i18n.translate('trainingScreens.fastTrackPrompt'),
    showCancelButton: true,
    cancelButtonText: i18n.translate('general.no'),
    confirmButtonText: i18n.translate('general.yes'),
    onClose: ({ dismiss, value }: SweetAlertResult) => {
      if (value === true) {
        if (!fastTrackData('totalTime')) {
          fastTrackData('startTime', Date.now());
        }
        onConfirmAsk();
      }
      if (dismiss) {
        onCancelAsk();
      }
    },
  };

  return (
    <React.Fragment>
      {fastTrackGrade && (
        <SweetAlert trigger={fastTrackFinished && fastTrackErrors !== -1 && grade.length > 0} {...gradeSwal} />
      )}
      <SweetAlert
        trigger={
          loaded &&
          hasFastTrack &&
          hasNoFoundationProgress &&
          !fastTrackFinished &&
          !fastTrackCanceled &&
          !fastTrackErrors &&
          !fastTrackInProgress
        }
        {...startSwal}
      />
    </React.Fragment>
  );
}
